import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ConnectModal } from "@mysten/dapp-kit";
import { Wallet, TrendingUp, Menu, X, LayoutGrid, User, HelpCircle, Copy, LogOut, Plus, Lock, Send, FileText, Sun, Moon, Trophy, Bell } from "lucide-react";
import { toast } from "sonner";
import { useWallet, useWalletBalance } from "@/context/WalletContext";
import { useTheme } from "@/context/ThemeContext";
import { api, shortAddr } from "@/lib/api";
import { fmtSui } from "@/lib/format";
import { isHidden } from "@/lib/perf";
import { pushSupported, notifPermission, enablePush, getEndpoint, isIOS, isStandalone } from "@/lib/push";
import logo from "@/assets/logo.png";
import { SOCIALS, XIcon } from "@/lib/socials";

export default function Header({ onCreate }) {
  const { connected, address, disconnect } = useWallet();
  const { realBalance } = useWalletBalance();
  const { theme, toggle } = useTheme();
  const [suiPrice, setSuiPrice] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const location = useLocation();

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => { getEndpoint().then((e) => setPushOn(!!e)); }, []);

  const toggleAlerts = async () => {
    if (pushOn) { toast("Notifications already on — manage per-coin alerts on each token page"); return; }
    if (isIOS() && !isStandalone()) {
      toast("On iPhone: tap Share → Add to Home Screen, then open SuiPads to enable alerts");
      return;
    }
    try {
      await enablePush({ address: address || null, topics: ["graduation", "price"] });
      setPushOn(true);
      toast.success("Notifications on — you'll get graduation & price alerts");
    } catch (e) {
      toast.error((e?.message || "Could not enable notifications").slice(0, 120));
    }
  };

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/sui-price");
        if (alive && data?.usd) setSuiPrice(data);
      } catch (e) {}
    };
    load();
    const t = setInterval(() => { if (!isHidden()) load(); }, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Award the daily points streak once per day per wallet (server dedups by UTC day too).
  useEffect(() => {
    if (!connected || !address) return;
    const key = `suipads_login_${address}`;
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(key) === today) return;
    api.post("/points/login", { address })
      .then(({ data }) => { localStorage.setItem(key, today); if (data?.awarded) toast.success(`+${data.points} points · daily streak 🔥`); })
      .catch(() => {});
  }, [connected, address]);

  return (
    <>
    <header className="sticky top-0 z-50 glass border-b border-white/10 px-4 sm:px-6 py-3">
      <div className="max-w-[1400px] mx-auto flex items-center gap-2 sm:gap-5">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
          <img src={logo} alt="SuiPads" className="w-9 h-9 rounded-xl object-contain ring-1 ring-primary/40 shadow-[0_0_16px_rgba(30,198,46,0.4)]" />
          <span className="font-display font-extrabold text-lg tracking-tight hidden sm:block brand-gradient">
            SuiPads
          </span>
        </Link>

        <div className="flex-1" />

        <nav className="hidden lg:flex items-center gap-0.5 shrink-0 px-1.5 py-1 rounded-full bg-white/5 border border-white/10">
          <Link to="/" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5 transition-colors"><LayoutGrid className="w-4 h-4" /> Board</Link>
          <Link to="/portfolio" className="flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium text-muted-foreground hover:text-white transition-colors" data-testid="nav-portfolio"><Wallet className="w-4 h-4" /> Portfolio</Link>
          <Link to="/vesting" className="flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium text-muted-foreground hover:text-white transition-colors" data-testid="nav-vesting"><Lock className="w-4 h-4" /> Vesting</Link>
          <Link to="/airdrop" className="flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium text-muted-foreground hover:text-white transition-colors" data-testid="nav-airdrop"><Trophy className="w-4 h-4" /> Airdrop</Link>
          {connected && <Link to={`/u/${address}`} className="flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium text-muted-foreground hover:text-white transition-colors" data-testid="nav-profile"><User className="w-4 h-4" /> Profile</Link>}
          <Link to="/how-it-works" className="flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium text-muted-foreground hover:text-white transition-colors"><HelpCircle className="w-4 h-4" /> How it Works</Link>
          <Link to="/whitepaper" data-testid="nav-whitepaper" className="flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium text-muted-foreground hover:text-white transition-colors"><FileText className="w-4 h-4" /> Whitepaper</Link>
          <div className="w-px h-5 bg-white/10 mx-1" />
          <a href={SOCIALS.x} target="_blank" rel="noreferrer" data-testid="nav-x" aria-label="X" className="p-2 rounded-full text-muted-foreground hover:text-[#1EC62E] transition-colors"><XIcon className="w-4 h-4" /></a>
          <a href={SOCIALS.telegram} target="_blank" rel="noreferrer" data-testid="nav-telegram" aria-label="Telegram" className="p-2 rounded-full text-muted-foreground hover:text-[#1EC62E] transition-colors"><Send className="w-4 h-4" /></a>
        </nav>

        {pushSupported() && (
          <button
            onClick={toggleAlerts}
            data-testid="notifications-toggle"
            aria-label="Notifications"
            title={pushOn ? "Notifications on" : "Enable notifications"}
            className={`shrink-0 p-2 rounded-full border transition-colors ${pushOn ? "bg-[#1EC62E]/15 border-[#1EC62E]/40 text-[#1EC62E]" : "bg-white/5 border-white/10 text-muted-foreground hover:text-foreground hover:border-primary/40"}`}
          >
            <Bell className="w-4 h-4" />
          </button>
        )}

        <button
          onClick={toggle}
          data-testid="theme-toggle-button"
          aria-label="Toggle theme"
          title={theme === "dark" ? "Switch to light" : "Switch to dark"}
          className="shrink-0 p-2 rounded-full bg-white/5 border border-white/10 text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
        >
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>

        <button
          onClick={onCreate}
          data-testid="nav-create-coin-button"
          className="shrink-0 flex items-center gap-1.5 px-2.5 sm:px-4 py-2 rounded-full font-bold text-xs sm:text-sm text-black transition-transform hover:scale-[1.03]"
          style={{ background: "linear-gradient(90deg,#1EC62E,#7CF24E)" }}
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Create Coin</span>
          <span className="sm:hidden">Create</span>
        </button>

        {connected ? (
          <button
            onClick={disconnect}
            data-testid="wallet-connect-button"
            title="Disconnect"
            className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 border border-[#1EC62E]/40 text-sm hover:bg-white/10 transition-colors"
          >
            <span className="w-2 h-2 rounded-full bg-[#1EC62E] pulse-dot" />
            <span className="font-mono font-bold hidden sm:inline">{fmtSui(realBalance, 3)} SUI</span>
            <span className="text-muted-foreground font-mono">{shortAddr(address)}</span>
          </button>
        ) : (
          <ConnectModal
            trigger={
              <button
                data-testid="wallet-connect-button"
                className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/15 text-sm font-bold hover:border-[#1EC62E]/50 transition-colors"
              >
                <Wallet className="w-4 h-4" /> <span className="hidden sm:inline">Connect Wallet</span>
              </button>
            }
          />
        )}

        <button onClick={() => setMenuOpen(true)} data-testid="mobile-menu-button" aria-label="Menu" className="lg:hidden shrink-0 p-2 rounded-full bg-white/5 border border-white/10 text-muted-foreground hover:text-white">
          <Menu className="w-5 h-5" />
        </button>
      </div>
    </header>

    {menuOpen && (
      <div className="lg:hidden fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm" onClick={() => setMenuOpen(false)}>
        <div className="absolute right-0 top-0 h-full w-80 max-w-[86vw] bg-background border-l border-white/10 flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mobile-menu-drawer">
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <Link to="/" className="flex items-center gap-2" onClick={() => setMenuOpen(false)}>
              <img src={logo} alt="SuiPads" className="w-8 h-8 rounded-lg object-contain" />
              <span className="font-display font-extrabold text-lg brand-gradient">SuiPads</span>
            </Link>
            <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" className="p-1.5 rounded-lg bg-white/5 text-muted-foreground hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          <div className="p-5 space-y-4">
            {/* Wallet card */}
            {connected ? (
              <div className="rounded-2xl border border-[#1EC62E]/30 p-4" style={{ background: "linear-gradient(135deg,rgba(0,255,135,0.08),transparent)" }} data-testid="drawer-wallet-card">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <span className="w-2 h-2 rounded-full bg-[#1EC62E] pulse-dot" /> Connected
                </div>
                <div className="font-mono font-extrabold text-2xl">{fmtSui(realBalance, 3)} <span className="text-sm text-muted-foreground">SUI</span></div>
                <div className="flex items-center justify-between mt-3">
                  <button onClick={() => { navigator.clipboard.writeText(address); toast.success("Address copied"); }} className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground hover:text-[#1EC62E]" data-testid="drawer-copy-address">
                    {shortAddr(address)} <Copy className="w-3 h-3" />
                  </button>
                  <button onClick={() => { disconnect(); setMenuOpen(false); }} className="flex items-center gap-1.5 text-xs font-bold text-[#F43F5E] hover:opacity-80" data-testid="drawer-disconnect">
                    <LogOut className="w-3.5 h-3.5" /> Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <ConnectModal trigger={
                <button className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-white/5 border border-white/15 font-bold hover:border-[#1EC62E]/50 transition-colors" data-testid="drawer-connect-wallet">
                  <Wallet className="w-4 h-4" /> Connect Wallet
                </button>
              } />
            )}

            {/* Nav */}
            <nav className="flex flex-col gap-1">
              {[
                { to: "/", label: "Board", icon: LayoutGrid, testid: "drawer-nav-board" },
                { to: "/portfolio", label: "Portfolio", icon: Wallet, testid: "drawer-nav-portfolio" },
                { to: "/vesting", label: "Vesting", icon: Lock, testid: "drawer-nav-vesting" },
                { to: "/airdrop", label: "Airdrop", icon: Trophy, testid: "drawer-nav-airdrop" },
                ...(connected ? [{ to: `/u/${address}`, label: "My Profile", icon: User, testid: "drawer-nav-profile" }] : []),
                { to: "/how-it-works", label: "How it Works", icon: HelpCircle, testid: "drawer-nav-how" },
                { to: "/whitepaper", label: "Whitepaper", icon: FileText, testid: "drawer-nav-whitepaper" },
              ].map((it) => {
                const active = location.pathname === it.to;
                return (
                  <Link key={it.label} to={it.to} data-testid={it.testid} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors ${active ? "bg-white/10 text-white" : "text-muted-foreground hover:bg-white/5 hover:text-white"}`}>
                    <it.icon className={`w-5 h-5 ${active ? "text-[#1EC62E]" : ""}`} /> {it.label}
                  </Link>
                );
              })}
            </nav>

            <button onClick={() => { setMenuOpen(false); onCreate && onCreate(); }} data-testid="drawer-create-coin" className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-display font-bold text-black transition-transform active:scale-[0.98]" style={{ background: "linear-gradient(90deg,#1EC62E,#7CF24E)" }}>
              <Plus className="w-5 h-5" /> Create Coin
            </button>

            <div className="flex items-center justify-center gap-3 pt-2">
              <a href={SOCIALS.x} target="_blank" rel="noreferrer" data-testid="drawer-x" aria-label="X" className="p-3 rounded-xl bg-white/5 border border-white/10 text-muted-foreground hover:text-[#1EC62E] transition-colors"><XIcon className="w-5 h-5" /></a>
              <a href={SOCIALS.telegram} target="_blank" rel="noreferrer" data-testid="drawer-telegram" aria-label="Telegram" className="p-3 rounded-xl bg-white/5 border border-white/10 text-muted-foreground hover:text-[#1EC62E] transition-colors"><Send className="w-5 h-5" /></a>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
